/**
 * Recipe-backed town-template families: the one place a generator family is
 * wired into the named-template texture owner. Body dispatch
 * (`townTemplateTexture.ts`), the cache suffix (`townPropTemplates.ts`) and the
 * effective texture key (`natureTextures.ts`) all resolve through here, so a
 * new family is one entry below instead of three hand-kept branches.
 */
import type { TownPropPalette } from '../../../../shared/scene/townPropTemplates';
import type { VegetationKind } from '../natureConfig';
import { buildTidalCascadeFountainBody, isTidalCascadeTemplate, tidalCascadeTextureSuffix, type TidalCascadeTemplate } from './tidalCascade/adapter';
import { buildHushStaticBody, hushStaticTextureSuffix, isHushStaticTemplate, type HushStaticTemplate } from './hushStatic';
import { buildHushDomesticBody, hushDomesticTextureSuffix, isHushDomesticTemplate, type HushDomesticTemplate } from './hushDomestic';
import { buildHushLanternBody, hushLanternTextureSuffix, isHushLanternTemplate, type HushLanternTemplate } from './hushLantern';
import { buildPieceBody, pieceTextureSuffix, isPieceTemplate, type PieceTemplate } from './pieces/adapter';

/** Every template owned by a recipe family. Extend with each new family. */
export type RecipeFamilyTemplate = TidalCascadeTemplate | HushStaticTemplate | HushDomesticTemplate | HushLanternTemplate | PieceTemplate;

export interface RecipeTemplateFamily<T extends string> {
  readonly id: string;
  readonly owns: (template: unknown) => template is T;
  readonly buildBody: (template: T, palette: TownPropPalette) => string;
  /** Starts with `__tpl_<template>_` and carries a content digest of the recipe. */
  readonly textureSuffix: (template: T, palette: TownPropPalette) => string;
  /** When set, the texture cache only uses the family suffix for this vegetation kind. */
  readonly vegetationKind?: VegetationKind;
}

type ErasedFamily = RecipeTemplateFamily<RecipeFamilyTemplate>;

function family<T extends RecipeFamilyTemplate>(f: RecipeTemplateFamily<T>): ErasedFamily {
  return f as unknown as ErasedFamily;
}

// Entries call through closures: adapters can import modules that reach this file (e.g. via the town
// template catalog), so a family's functions may still be undefined while this module first evaluates.
export const RECIPE_TEMPLATE_FAMILIES: readonly ErasedFamily[] = [
  family<TidalCascadeTemplate>({
    id: 'tidal_cascade',
    owns: (t): t is TidalCascadeTemplate => isTidalCascadeTemplate(t),
    buildBody: (t, p) => buildTidalCascadeFountainBody(t, p),
    textureSuffix: (t, p) => tidalCascadeTextureSuffix(t, p),
    vegetationKind: 'village_well',
  }),
  family<HushDomesticTemplate>({
    id: 'hush_domestic',
    owns: (t): t is HushDomesticTemplate => isHushDomesticTemplate(t),
    buildBody: (t, p) => buildHushDomesticBody(t, p),
    textureSuffix: (t, p) => hushDomesticTextureSuffix(t, p),
  }),
  family<HushStaticTemplate>({
    id: 'hush_static',
    owns: (t): t is HushStaticTemplate => isHushStaticTemplate(t),
    buildBody: (t, p) => buildHushStaticBody(t, p),
    textureSuffix: (t, p) => hushStaticTextureSuffix(t, p),
  }),
  family<HushLanternTemplate>({
    id: 'hush_lantern',
    owns: (t): t is HushLanternTemplate => isHushLanternTemplate(t),
    buildBody: (t, p) => buildHushLanternBody(t, p),
    textureSuffix: (t, p) => hushLanternTextureSuffix(t, p),
  }),
  family<PieceTemplate>({
    id: 'pieces',
    owns: (t): t is PieceTemplate => isPieceTemplate(t),
    buildBody: (t, p) => buildPieceBody(t, p),
    textureSuffix: (t, p) => pieceTextureSuffix(t, p),
  }),
];

/** The family that owns `template`, if any. */
export function recipeFamilyFor(template: unknown): ErasedFamily | undefined {
  return RECIPE_TEMPLATE_FAMILIES.find(f => f.owns(template));
}

export function isRecipeFamilyTemplate(template: unknown): template is RecipeFamilyTemplate {
  return recipeFamilyFor(template) !== undefined;
}
